import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface CreateSeriesParams {
  title: string;
  description?: string;
  postIds?: string[];
  isPublic?: boolean;
  coverImage?: string;
  tags?: string[];
}

interface UpdateSeriesParams {
  title?: string;
  description?: string | null;
  isPublic?: boolean;
  coverImage?: string | null;
  tags?: string[];
}

interface SeriesStats {
  totalPosts: number;
  totalLikes: number;
  totalComments: number;
  totalViews: number;
  averageEngagement: number;
  firstPublished?: string;
  lastPublished?: string;
}

interface Post {
  id: string;
  title: string;
  content: string;
  publishedAt?: string;
  tags: string[];
}

interface SeriesWithDetails {
  id: string;
  title: string;
  description?: string;
  isPublic: boolean;
  coverImage?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  posts: Post[];
  stats: SeriesStats;
}

interface SeriesSummary {
  id: string;
  title: string;
  description?: string;
  isPublic: boolean;
  coverImage?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  postCount: number;
  stats: SeriesStats;
}

interface SeriesNavigation {
  currentIndex: number;
  totalPosts: number;
  previousPost: { id: string; title: string } | null;
  nextPost: { id: string; title: string } | null;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * Create a new content series
 */
export const createSeries = async (params: CreateSeriesParams): Promise<SeriesWithDetails> => {
  const response = await axios.post(`${API_URL}/api/series`, params, getAuthHeaders());
  return response.data;
};

/**
 * Get series by ID
 */
export const getSeries = async (seriesId: string): Promise<SeriesWithDetails> => {
  try {
    // Try with auth first
    const response = await axios.get(`${API_URL}/api/series/${seriesId}`, getAuthHeaders());
    return response.data;
  } catch (error: any) {
    // If unauthorized, try without auth (for public series)
    if (error.response?.status === 401) {
      const response = await axios.get(`${API_URL}/api/series/${seriesId}`);
      return response.data;
    }
    throw error;
  }
};

/**
 * Get all series for a user
 */
export const getUserSeries = async (userId: string): Promise<SeriesSummary[]> => {
  try {
    // Try with auth first (to see private series if viewing own)
    const response = await axios.get(`${API_URL}/api/series/user/${userId}`, getAuthHeaders());
    return response.data;
  } catch (error: any) {
    // If unauthorized, try without auth (public series only)
    if (error.response?.status === 401) {
      const response = await axios.get(`${API_URL}/api/series/user/${userId}`);
      return response.data;
    }
    throw error;
  }
};

/**
 * Search series
 */
export const searchSeries = async (query: string): Promise<any[]> => {
  try {
    // Try with auth first
    const response = await axios.get(
      `${API_URL}/api/series/search?q=${encodeURIComponent(query)}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error: any) {
    // If unauthorized, try without auth
    if (error.response?.status === 401) {
      const response = await axios.get(`${API_URL}/api/series/search?q=${encodeURIComponent(query)}`);
      return response.data;
    }
    throw error;
  }
};

/**
 * Update series metadata
 */
export const updateSeries = async (seriesId: string, params: UpdateSeriesParams): Promise<SeriesWithDetails> => {
  const response = await axios.put(`${API_URL}/api/series/${seriesId}`, params, getAuthHeaders());
  return response.data;
};

/**
 * Delete series
 */
export const deleteSeries = async (seriesId: string): Promise<{ message: string }> => {
  const response = await axios.delete(`${API_URL}/api/series/${seriesId}`, getAuthHeaders());
  return response.data;
};

/**
 * Add post to series
 */
export const addPostToSeries = async (
  seriesId: string,
  postId: string,
  order?: number
): Promise<SeriesWithDetails> => {
  const response = await axios.post(
    `${API_URL}/api/series/${seriesId}/posts`,
    { postId, order },
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Remove post from series
 */
export const removePostFromSeries = async (seriesId: string, postId: string): Promise<SeriesWithDetails> => {
  const response = await axios.delete(`${API_URL}/api/series/${seriesId}/posts/${postId}`, getAuthHeaders());
  return response.data;
};

/**
 * Reorder posts in series
 */
export const reorderSeries = async (
  seriesId: string,
  postOrders: Array<{ postId: string; order: number }>
): Promise<SeriesWithDetails> => {
  const response = await axios.put(
    `${API_URL}/api/series/${seriesId}/reorder`,
    { postOrders },
    getAuthHeaders()
  );
  return response.data;
};

/**
 * Get series navigation (previous/next posts)
 */
export const getSeriesNavigation = async (seriesId: string, postId: string): Promise<SeriesNavigation> => {
  const response = await axios.get(`${API_URL}/api/series/${seriesId}/navigation/${postId}`);
  return response.data;
};

export default {
  createSeries,
  getSeries,
  getUserSeries,
  searchSeries,
  updateSeries,
  deleteSeries,
  addPostToSeries,
  removePostFromSeries,
  reorderSeries,
  getSeriesNavigation,
};
